import GradingIcon from '@mui/icons-material/Grading';
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card, useToast } from "randmarcomps";
import {
  useGetV4WarehousesQuery,
  usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation,
  usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation,
} from "randmar-api-client";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { PurchaseOrderPartNumber, RequisitionPartNumber } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import ButtonWithDialogConfirm from '../util/ButtonWithDialogConfirm';
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";

interface IProps {
  store?: RootStore;
  purchaseOrdersPartNumbers?: PurchaseOrderPartNumber[];
  requisitionsPartNumbers?: RequisitionPartNumber[];
  applicationId: string;
}

const OpenPurchaseOrdersGrid: React.FC<IProps> = ({ store, purchaseOrdersPartNumbers, requisitionsPartNumbers, applicationId }) => {
  const { productStore } = store!;
  const { toast } = useToast();
  const { data: warehouses } = useGetV4WarehousesQuery();

  const [editPurchaseOrderItem] = usePostV4ManufacturerByRouteManufacturerIdPurchaseOrderAndPurchaseOrderNumberEditItemRandmarSkuMutation();
  const [editRequisitionItem] = usePostV4ManufacturerByRouteManufacturerIdRequisitionAndRequisitionNumberEditItemRandmarSkuMutation();

  const getWarehouseNameFromCode = React.useCallback((code?: string): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleRemoveFromPurchaseOrder = React.useCallback(async (purchaseOrderNumber: string, randmarSKU: string) => {
    try {
      await editPurchaseOrderItem({
        routeManufacturerId: applicationId,
        purchaseOrderNumber,
        randmarSku: randmarSKU,
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Item removed from purchase order." });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to remove item from purchase order." });
    }

    await productStore.getProduct(applicationId, randmarSKU);
  }, [applicationId, editPurchaseOrderItem, productStore, toast]);

  const handleRemoveFromRequisition = React.useCallback(async (requisitionNumber: string, randmarSKU: string) => {
    try {
      await editRequisitionItem({
        routeManufacturerId: applicationId,
        requisitionNumber,
        randmarSku: randmarSKU,
        quantity: 0,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Item removed from requisition." });
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to remove item from requisition." });
    }

    await productStore.getProduct(applicationId, randmarSKU);
  }, [applicationId, editRequisitionItem, productStore, toast]);

  if ((purchaseOrdersPartNumbers || []).length + (requisitionsPartNumbers || []).length === 0) {
    return <div>There are no pending purchase orders at the present time...</div>;
  }

  return (
    <Card className="w-full bg-white">
      <div className="rounded-[20px] w-full overflow-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Part Number</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Date</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity Ordered</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Unit Price</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {requisitionsPartNumbers && requisitionsPartNumbers
              .slice()
              .map((row, index) => (
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Requisition" + index}>
                  <TableCell align="center"><Link title={`Draft purchase ${row.requisitionNumber}`} to={Path.document(applicationId, row.requisitionNumber)}><GradingIcon color="inherit" /></Link></TableCell>
                  <TableCell align="center">{getWarehouseNameFromCode(row.location)}</TableCell>
                  <TableCell align="center"><Link to={Path.product(applicationId, row.randmarSKU)}>{row.mpn}</Link></TableCell>
                  <TableCell align="center">N/A</TableCell>
                  <TableCell align="center">{row.quantityOrdered}</TableCell>
                  <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} /></TableCell>
                  <TableCell align="center">
                    <ButtonWithDialogConfirm
                      fab={true}
                      onConfirmation={() => handleRemoveFromRequisition(row.requisitionNumber || "", row.randmarSKU || "")}
                      buttonTitle={`Remove ${row.mpn} from ${row.requisitionNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            {purchaseOrdersPartNumbers && purchaseOrdersPartNumbers
              .slice()
              .map((row, index) => (
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"PurchaseOrder" + index}>
                  <TableCell align="center">
                    <Link title="Purchase Order Number" to={Path.document(applicationId, row.purchaseOrderNumber)}>{row.purchaseOrderNumber}</Link>
                  </TableCell>
                  <TableCell align="center">{getWarehouseNameFromCode(row.location)}</TableCell>
                  <TableCell align="center">
                    <Link to={Path.product(applicationId, row.randmarSKU)}>{row.mpn}</Link>
                  </TableCell>
                  <TableCell align="center">{FormatDateFromInt(row.purchaseOrderDate)}</TableCell>
                  <TableCell align="center">{row.quantity}</TableCell>
                  <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={row.unitPrice || 0} /></TableCell>
                  <TableCell align="center">
                    <ButtonWithDialogConfirm
                      fab={true}
                      onConfirmation={() => handleRemoveFromPurchaseOrder(row.purchaseOrderNumber || "", row.randmarSKU || "")}
                      buttonTitle={`Remove ${row.mpn} from ${row.purchaseOrderNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default withManufacturer(inject("store")(observer(OpenPurchaseOrdersGrid)));
